<template>
  <div class="modal" :class="{ 'is-active': isOpen }">
    <div class="modal-background" @click="handleClose"></div>
    <div class="modal-card">
      <!-- Modal Header -->
      <header class="modal-card-head">
        <p class="modal-card-title">
          <i class="fa fa-chart-line mr-2"></i>
          {{ $t("evaluation details") }}
        </p>
        <button class="delete" @click="handleClose" aria-label="close"></button>
      </header>

      <!-- Modal Body -->
      <section class="modal-card-body">
        <!-- Loading State -->
        <LoadingSpinner v-if="isLoading" />

        <!-- Content -->
        <div v-else-if="evaluation" class="evaluation-content">
          <!-- Customer Information Section -->
          <CustomerInfoSection :evaluation="evaluation" />

          <!-- Evaluation Results Section -->
          <EvaluationResultsSection
            :evaluation="evaluation"
            :has-evaluation-detail="hasEvaluationDetail"
          />

          <!-- Timeline Section -->
          <TimelineSection :evaluation="evaluation" />
        </div>

        <!-- Error State -->
        <div v-else class="notification is-warning">
          <i class="fa fa-exclamation-triangle mr-2"></i>
          {{ $t("evaluation not found") }}
        </div>
      </section>

      <!-- Modal Footer -->
      <footer class="modal-card-foot">
        <div class="buttons">
          <!-- <button
            class="button is-primary"
            @click="handleEdit"
            v-if="evaluation && canEdit"
          >
            <i class="fa fa-edit mr-1"></i>
            {{ $t("edit evaluation") }}
          </button>

          <button
            class="button is-warning"
            @click="handleUpload"
            v-if="evaluation && !evaluation.pdf_file_path"
          >
            <i class="fa fa-upload mr-1"></i>
            {{ $t("upload pdf") }}
          </button> -->

          <button class="button" @click="handleClose">
            <i class="fa fa-times mr-1"></i>
            {{ $t("close") }}
          </button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import { get, error_message } from "@/utils";
import moment from "moment";

// Components
import LoadingSpinner from "./components/LoadingSpinner.vue";
import CustomerInfoSection from "./components/CustomerInfoSection.vue";
import EvaluationResultsSection from "./components/EvaluationResultsSection.vue";
import TimelineSection from "./components/TimelineSection.vue";

// Constants
const RESULT_CLASSES = {
  approved: "is-success",
  rejected: "is-danger",
  pending_review: "is-warning",
  unset: "is-dark",
};

const STATUS_CLASSES = {
  draft: "is-dark",
  completed: "is-success",
  archived: "is-dark",
};

export default {
  name: "CreditAnalysisEvaluationDetailModal",

  components: {
    LoadingSpinner,
    CustomerInfoSection,
    EvaluationResultsSection,
    TimelineSection,
  },

  props: {
    canEdit: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["edit", "upload", "close"],

  data() {
    return {
      // Modal state
      isOpen: false,
      isLoading: false,

      // Data
      uuid: null,
      evaluation: null,

      // Error handling
      pdfLoadError: false,
    };
  },

  computed: {
    /**
     * Check if evaluation has detail data
     */
    hasEvaluationDetail() {
      return (
        this.evaluation?.evaluation_detail &&
        Array.isArray(this.evaluation.evaluation_detail) &&
        this.evaluation.evaluation_detail.length > 0
      );
    },

    /**
     * Get formatted evaluation status
     */
    formattedStatus() {
      return this.evaluation?.status || "draft";
    },

    /**
     * Get formatted evaluation result
     */
    formattedResult() {
      return this.evaluation?.final_result || "unset";
    },
  },

  methods: {
    // ===================
    // Public Methods
    // ===================

    /**
     * Open modal with evaluation data
     * @param {string} uuid - Evaluation UUID
     */
    open(uuid) {
      if (!uuid) {
        console.warn("CreditAnalysisEvaluationDetailModal: UUID is required");
        return;
      }

      this.uuid = uuid;
      this.isOpen = true;
      this.resetState();
      this.loadEvaluation();
    },

    /**
     * Close modal and reset state
     */
    handleClose() {
      this.isOpen = false;
      this.resetState();
      this.$emit("close");
    },

    // ===================
    // Data Methods
    // ===================

    /**
     * Load evaluation data from API
     */
    async loadEvaluation() {
      if (!this.uuid) {
        console.error("CreditAnalysisEvaluationDetailModal: No UUID provided");
        return;
      }

      this.isLoading = true;

      try {
        const response = await get("/credit-analysis/evaluation", {
          uuid: this.uuid,
        });

        this.evaluation = response;
      } catch (error) {
        console.error("Failed to load evaluation:", error);
        error_message(error);
        this.evaluation = null;
      } finally {
        this.isLoading = false;
      }
    },

    // ===================
    // Event Handlers
    // ===================

    /**
     * Handle edit evaluation action
     */
    handleEdit() {
      if (!this.evaluation?.uuid) return;

      this.$emit("edit", this.evaluation.uuid);
      this.handleClose();
    },

    /**
     * Handle upload PDF action
     */
    handleUpload() {
      if (!this.evaluation?.uuid) return;

      this.$emit("upload", this.evaluation.uuid);
      this.handleClose();
    },

    /**
     * Handle PDF load error
     */
    onPdfLoadError() {
      this.pdfLoadError = true;
    },

    // ===================
    // Utility Methods
    // ===================

    /**
     * Reset component state
     */
    resetState() {
      this.evaluation = null;
      this.pdfLoadError = false;
      this.isLoading = false;
    },

    /**
     * Format date using moment
     * @param {string|Date} date - Date to format
     * @returns {string} Formatted date
     */
    formatDate(date) {
      if (!date) return "";
      return moment(date).format("DD/MM/YYYY HH:mm");
    },

    /**
     * Get CSS class for evaluation result
     * @param {string} result - Evaluation result
     * @returns {string} CSS class
     */
    getResultClass(result) {
      return RESULT_CLASSES[result] || RESULT_CLASSES.unset;
    },

    /**
     * Get CSS class for evaluation status
     * @param {string} status - Evaluation status
     * @returns {string} CSS class
     */
    getStatusClass(status) {
      return STATUS_CLASSES[status] || STATUS_CLASSES.draft;
    },
  },

  // ===================
  // Lifecycle Hooks
  // ===================

  beforeUnmount() {
    // Clean up any pending requests or intervals
    this.resetState();
  },
};
</script>

<style scoped>
/* ===================
   Bulma Dark Mode Variables
   =================== */
:root {
  --bulma-scheme-main: hsl(0, 0%, 21%);
  --bulma-scheme-main-bis: hsl(0, 0%, 14%);
  --bulma-scheme-main-ter: hsl(0, 0%, 10%);
  --bulma-background: hsl(0, 0%, 21%);
  --bulma-border: hsl(0, 0%, 42%);
  --bulma-border-weak: hsl(0, 0%, 29%);
  --bulma-text: hsl(0, 0%, 96%);
  --bulma-text-weak: hsl(0, 0%, 71%);
  --bulma-text-strong: hsl(0, 0%, 100%);
  --bulma-primary: hsl(171, 100%, 41%);
  --bulma-primary-dark: hsl(171, 100%, 29%);
  --bulma-link: hsl(229, 53%, 71%);
  --bulma-success: hsl(153, 53%, 53%);
  --bulma-warning: hsl(44, 100%, 77%);
  --bulma-danger: hsl(348, 86%, 61%);
}

/* ===================
   Modal Layout
   =================== */
.modal-card {
  width: 95vw;
  height: 95vh;
  max-width: 1200px;
  max-height: 95vh;
  margin: 0;
  border-radius: 20px;
  box-shadow: 0px 0px 10px 2px rgba(0, 209, 178, 0.8);
  background-color: var(--bulma-scheme-main);
  border: 1px solid var(--bulma-border-weak);
}

.modal-card-head {
  flex-shrink: 0;
  padding: 1.5rem 2rem;
  background-color: var(--bulma-scheme-main-bis);
  color: var(--bulma-text-strong);
  border-radius: 6px 6px 0 0;
  border-bottom: 1px solid var(--bulma-border-weak);
}

.modal-card-title {
  color: var(--bulma-text-strong);
  font-weight: 600;
  font-size: 1.25rem;
  margin-bottom: 0;
}

.modal-card-head .delete {
  background: var(--bulma-scheme-main-ter);
  border: 1px solid var(--bulma-border-weak);
}

.modal-card-head .delete:hover,
.modal-card-head .delete:focus {
  background: var(--bulma-border);
}

.modal-card-head .delete::before,
.modal-card-head .delete::after {
  background-color: var(--bulma-text);
}

/* ===================
   Modal Body
   =================== */
.modal-card-body {
  height: calc(95vh - 140px);
  max-height: none;
  overflow-y: auto;
  padding: 0;
  background-color: var(--bulma-background);
}

.evaluation-content {
  padding: 2rem;
}

/* ===================
   Modal Footer
   =================== */
.modal-card-foot {
  flex-shrink: 0;
  padding: 1.5rem 2rem;
  background-color: var(--bulma-scheme-main-bis);
  border-top: 1px solid var(--bulma-border-weak);
  border-radius: 0 0 6px 6px;
}

.buttons {
  margin-bottom: 0;
}

.button {
  transition: all 0.2s ease;
  background-color: var(--bulma-scheme-main);
  border-color: var(--bulma-border-weak);
  color: var(--bulma-text);
}

.button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.25);
  background-color: var(--bulma-scheme-main-bis);
  border-color: var(--bulma-border);
}

.button.is-primary {
  background-color: var(--bulma-primary);
  border-color: var(--bulma-primary);
  color: var(--bulma-scheme-main);
}

.button.is-primary:hover {
  background-color: var(--bulma-primary-dark);
  border-color: var(--bulma-primary-dark);
}

.button.is-warning {
  background-color: var(--bulma-warning);
  border-color: var(--bulma-warning);
  color: var(--bulma-scheme-main);
}

/* ===================
   Content Sections
   =================== */
.box {
  margin-bottom: 1.5rem;
  border-radius: 6px;
  box-shadow: 0 0.5em 1em -0.125em rgba(10, 10, 10, 0.1),
    0 0px 0 1px rgba(10, 10, 10, 0.02);
  border: 1px solid var(--bulma-border-weak);
  background-color: var(--bulma-scheme-main);
}

/* ===================
   Notifications
   =================== */
.notification {
  background-color: var(--bulma-scheme-main);
  color: var(--bulma-text);
  border: 1px solid var(--bulma-border-weak);
}

.notification.is-warning {
  background-color: rgba(255, 221, 87, 0.1);
  color: var(--bulma-warning);
  border-color: var(--bulma-warning);
}

/* ===================
   Responsive Design
   =================== */
@media screen and (max-width: 768px) {
  .modal-card {
    width: 100vw;
    height: 100vh;
    max-width: 100vw;
    max-height: 100vh;
    border-radius: 0;
  }

  .modal-card-head {
    border-radius: 0;
  }

  .modal-card-foot {
    border-radius: 0;
  }

  .evaluation-content {
    padding: 1rem;
  }

  .modal-card-body {
    height: calc(100vh - 140px);
  }
}

/* ===================
   Utility Classes
   =================== */
.mr-1 {
  margin-right: 0.25rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

.mb-3 {
  margin-bottom: 0.75rem;
}

.mb-4 {
  margin-bottom: 1rem;
}

/* ===================
   Animations
   =================== */
.modal.is-active .modal-card {
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-50px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* ===================
   Scrollbar Styling (Dark Mode)
   =================== */
.modal-card-body::-webkit-scrollbar {
  width: 8px;
}

.modal-card-body::-webkit-scrollbar-track {
  background: var(--bulma-scheme-main-ter);
  border-radius: 4px;
}

.modal-card-body::-webkit-scrollbar-thumb {
  background: var(--bulma-border);
  border-radius: 4px;
}

.modal-card-body::-webkit-scrollbar-thumb:hover {
  background: var(--bulma-text-weak);
}
</style>
